import React, { useState, useEffect, useCallback } from 'react';
import { boardsAPI } from '../api';

/**
 * FinalDecisionPanel — replaces the legacy checkbox toggle in BoardEditor.
 *
 * States:
 *   - locked      : plan does not allow Final Decision Mode
 *   - inactive    : feature available, no invitation yet -> [Enable secure final approvals]
 *   - resecure    : legacy board, mode previously toggled without invite -> banner + CTA
 *   - active      : invitation sent -> client email + invited date + audit
 */
const FMT_DATE = (iso, lang = 'en') => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(
      lang === 'pl' ? 'pl-PL' : lang === 'de' ? 'de-DE' : 'en-GB',
      { year: 'numeric', month: 'short', day: 'numeric' },
    );
  } catch (e) { return ''; }
};
const FMT_DATETIME = (iso, lang = 'en') => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(
      lang === 'pl' ? 'pl-PL' : lang === 'de' ? 'de-DE' : 'en-GB',
      { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
    );
  } catch (e) { return ''; }
};

const ACTION_KEY = {
  invited: 'fd_audit_invited',
  resent: 'fd_audit_resent',
  revoked: 'fd_audit_revoked',
  opened: 'fd_audit_opened',
  marked_final: 'fd_audit_marked_final',
  unmarked_final: 'fd_audit_unmarked_final',
  confirmed: 'fd_audit_confirmed',
  mode_disabled: 'fd_audit_mode_disabled',
};

const InviteModal = ({ t, initialEmail = '', loading, onClose, onSubmit, errorText }) => {
  const [email, setEmail] = useState(initialEmail);
  const [touched, setTouched] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content fade-in" data-testid="fd-invite-modal" style={{ maxWidth: 460 }}>
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '22px',
          marginBottom: '14px',
          textAlign: 'center',
          letterSpacing: '0.01em',
        }}>
          {t.fd_modal_title}
        </h2>
        <p style={{
          fontSize: '13px',
          color: 'var(--kahla-text-light)',
          textAlign: 'center',
          lineHeight: 1.7,
          margin: '0 0 26px',
          whiteSpace: 'pre-line',
        }}>
          {t.fd_modal_body}
        </p>

        <div className="form-group" style={{ marginBottom: 20 }}>
          <label style={{
            fontSize: '11px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--kahla-text-light)',
          }}>{t.fd_modal_email_label}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="client@example.com"
            data-testid="fd-invite-email-input"
            autoFocus
            style={{ marginTop: 6 }}
          />
          {touched && !valid && (
            <p style={{ fontSize: '11px', color: '#a85a5a', marginTop: 6 }}>{t.fd_invalid_email}</p>
          )}
          {errorText && (
            <p style={{ fontSize: '11px', color: '#a85a5a', marginTop: 6 }}>{errorText}</p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            style={{ flex: 1 }}
            data-testid="fd-invite-cancel-btn"
          >
            {t.fd_modal_cancel}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!valid || loading}
            onClick={() => onSubmit(email.trim().toLowerCase())}
            style={{ flex: 1 }}
            data-testid="fd-invite-submit-btn"
          >
            {loading ? '…' : t.fd_modal_send}
          </button>
        </div>
      </div>
    </>
  );
};

const AuditList = ({ entries, t, lang }) => {
  const [open, setOpen] = useState(false);
  if (!entries) return null;

  return (
    <div style={{ marginTop: 18, borderTop: '1px solid var(--kahla-border)', paddingTop: 16 }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="fd-audit-toggle"
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: '11px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--kahla-text-light)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          backgroundColor: open ? '#C9A96E' : 'var(--kahla-border)',
          transition: 'background-color 0.25s',
        }} />
        {t.fd_audit_title}
        <span style={{ marginLeft: 'auto', fontSize: '9px', opacity: 0.6 }}>
          {open ? t.fd_audit_hide : t.fd_audit_show}
        </span>
      </button>

      {open && (
        <div
          data-testid="fd-audit-list"
          style={{ marginTop: 12, maxHeight: 240, overflowY: 'auto' }}
        >
          {entries.length === 0 ? (
            <p style={{
              fontSize: '12px', color: 'var(--kahla-text-light)',
              fontStyle: 'italic', margin: 0, textAlign: 'center', padding: '12px 0',
            }}>
              {t.fd_audit_empty}
            </p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {entries.map((e) => (
                <li
                  key={e.id}
                  style={{
                    padding: '10px 0',
                    borderBottom: '1px solid var(--kahla-cream)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <span style={{ fontSize: '12px', color: 'var(--kahla-text)' }}>
                    {t[ACTION_KEY[e.action]] || e.action}
                    {e.image_filename && (
                      <span style={{ color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>
                        {' \u2014 '}{e.image_filename}
                      </span>
                    )}
                  </span>
                  <span style={{
                    fontSize: '10px',
                    letterSpacing: '0.06em',
                    color: 'var(--kahla-text-light)',
                  }}>
                    {FMT_DATETIME(e.timestamp, lang)}
                    {e.email && (
                      <span> · {e.email}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const FinalDecisionPanel = ({
  board,
  t,
  lang = 'en',
  featureAccess,
  onChange, // notify parent of final_decision_mode/needs_resecure changes
  LockOverlay,
}) => {
  const [showInvite, setShowInvite] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 'idle' | 'sending' | 'sent' — drives the resend-button feedback so
  // the user sees the click "land" (was previously a silent no-op
  // visually). Auto-resets to 'idle' a couple of seconds after the
  // success state appears.
  const [resendState, setResendState] = useState('idle');
  const [error, setError] = useState('');

  const planLocked = !featureAccess?.allow_final_decision_mode;

  const refreshStatus = useCallback(async () => {
    if (!board?.id) return;
    setLoading(true);
    try {
      const res = await boardsAPI.finalDecision.status(board.id);
      setStatus(res.data);
    } catch (e) {
      console.warn('Failed to load final decision status', e);
    } finally {
      setLoading(false);
    }
  }, [board?.id]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const handleInvite = async (email) => {
    setSubmitting(true);
    setError('');
    try {
      await boardsAPI.finalDecision.invite(board.id, email);
      setShowInvite(false);
      await refreshStatus();
      if (onChange) onChange({ final_decision_mode: true, final_decision_needs_resecure: false });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!status?.invitation) return;
    setSubmitting(true);
    setResendState('sending');
    try {
      await boardsAPI.finalDecision.resend(board.id);
      await refreshStatus();
      setResendState('sent');
      // Hold the success state long enough to be unmistakable, then
      // quietly return to idle so the studio can resend again.
      setTimeout(() => setResendState('idle'), 2500);
    } catch (e) {
      setResendState('idle');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm(`${t.fd_revoke}?`)) return;
    setSubmitting(true);
    try {
      await boardsAPI.finalDecision.revoke(board.id);
      await refreshStatus();
      if (onChange) onChange({ final_decision_needs_resecure: true });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm(`${t.fd_disable_cta}?`)) return;
    setSubmitting(true);
    try {
      await boardsAPI.finalDecision.disable(board.id);
      await refreshStatus();
      if (onChange) onChange({ final_decision_mode: false, final_decision_needs_resecure: false });
    } finally {
      setSubmitting(false);
    }
  };

  const isActive = status?.final_decision_mode && status?.invitation;
  const needsResecure = status?.needs_resecure || board?.final_decision_needs_resecure;

  // ===== render =====
  return (
    <div className="settings-panel" data-testid="final-decision-panel" style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <p className="settings-panel-title" style={{ marginBottom: 0 }}>
          {t.final_decision_mode}
        </p>
        {isActive && (
          <span style={{
            fontSize: '10px',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: '#C9A96E',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              backgroundColor: '#C9A96E',
              boxShadow: '0 0 0 3px rgba(201,169,110,0.18)',
            }} />
            {t.fd_status_active}
          </span>
        )}
      </div>

      <p style={{
        fontSize: '11px',
        color: 'var(--kahla-text-light)',
        marginTop: 8,
        marginBottom: 16,
        lineHeight: 1.7,
      }}>
        {t.final_decision_desc}
      </p>

      {/* === Locked by plan === */}
      {planLocked && (
        <>
          <div style={{
            padding: '10px 0',
            fontSize: '12px',
            color: 'var(--kahla-text-light)',
            opacity: 0.65,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            🔒 {t.final_decision_mode}
          </div>
          {LockOverlay && <LockOverlay />}
        </>
      )}

      {/* === Re-secure banner (legacy boards) === */}
      {!planLocked && !isActive && needsResecure && (
        <div
          data-testid="fd-resecure-banner"
          style={{
            border: '1px solid #d9c8a3',
            background: 'rgba(201,169,110,0.06)',
            padding: '14px 16px',
            marginBottom: 14,
          }}
        >
          <p style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '14px',
            margin: '0 0 6px',
            color: 'var(--kahla-text)',
          }}>
            {t.fd_resecure_banner_title}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--kahla-text-light)', lineHeight: 1.7, margin: '0 0 12px' }}>
            {t.fd_resecure_banner_body}
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowInvite(true)}
            data-testid="fd-resecure-cta"
            style={{ fontSize: '11px', padding: '8px 18px' }}
          >
            {t.fd_resecure_banner_cta}
          </button>
        </div>
      )}

      {/* === Inactive (no invite, no legacy flag) === */}
      {!planLocked && !isActive && !needsResecure && (
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowInvite(true)}
          disabled={loading}
          data-testid="fd-enable-cta"
          style={{ width: '100%', fontSize: '12px', letterSpacing: '0.06em' }}
        >
          {t.fd_enable_cta}
        </button>
      )}

      {/* === Active === */}
      {!planLocked && isActive && (
        <div data-testid="fd-active-panel">
          <div style={{
            padding: '14px 0',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            rowGap: 8,
            columnGap: 16,
            fontSize: '12px',
            lineHeight: 1.6,
            borderTop: '1px solid var(--kahla-cream)',
            borderBottom: '1px solid var(--kahla-cream)',
          }}>
            <span style={{
              color: 'var(--kahla-text-light)',
              letterSpacing: '0.1em', textTransform: 'uppercase',
              fontSize: '10px', alignSelf: 'center',
            }}>{t.fd_client_label}</span>
            <span style={{ color: 'var(--kahla-text)', wordBreak: 'break-all' }}>
              {status.invitation.email}
            </span>
            <span style={{
              color: 'var(--kahla-text-light)',
              letterSpacing: '0.1em', textTransform: 'uppercase',
              fontSize: '10px', alignSelf: 'center',
            }}>{t.fd_invited_label}</span>
            <span style={{ color: 'var(--kahla-text)' }}>
              {FMT_DATE(status.invitation.invited_at, lang)}
            </span>
          </div>

          <div style={{
            display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap',
          }}>
            <button
              type="button"
              onClick={handleResend}
              disabled={submitting}
              data-testid="fd-resend-btn"
              aria-busy={resendState === 'sending'}
              style={{
                flex: '1 1 auto',
                background: resendState === 'sent'
                  ? 'rgba(201,169,110,0.12)'
                  : resendState === 'sending'
                  ? 'rgba(201,169,110,0.06)'
                  : 'none',
                border: `1px solid ${
                  resendState === 'sent'
                    ? 'rgba(201,169,110,0.55)'
                    : 'var(--kahla-border)'
                }`,
                padding: '8px 14px',
                fontSize: '11px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: resendState === 'sent'
                  ? '#8a6f3a'
                  : 'var(--kahla-text)',
                cursor: submitting ? 'wait' : 'pointer',
                transition:
                  'background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {resendState === 'sending' && (
                <span
                  aria-hidden="true"
                  data-testid="fd-resend-spinner"
                  style={{
                    display: 'inline-block',
                    width: 10, height: 10,
                    borderRadius: '50%',
                    border: '1.5px solid rgba(74,69,64,0.25)',
                    borderTopColor: 'rgba(74,69,64,0.85)',
                    animation: 'spin 0.7s linear infinite',
                  }}
                />
              )}
              {resendState === 'sent' && (
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: 13,
                    lineHeight: 1,
                    color: '#8a6f3a',
                  }}
                >✓</span>
              )}
              <span>
                {resendState === 'sending'
                  ? (t.fd_resending || (lang === 'pl' ? 'Wysyłanie...' : lang === 'de' ? 'Wird gesendet...' : 'Sending...'))
                  : resendState === 'sent'
                  ? (t.fd_audit_resent || (lang === 'pl' ? 'Wysłano ponownie' : lang === 'de' ? 'Erneut gesendet' : 'Sent'))
                  : t.fd_resend}
              </span>
            </button>
            <button
              type="button"
              onClick={handleRevoke}
              disabled={submitting}
              data-testid="fd-revoke-btn"
              style={{
                flex: '1 1 auto',
                background: 'none',
                border: '1px solid var(--kahla-border)',
                padding: '8px 14px',
                fontSize: '11px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--kahla-text-light)',
                cursor: 'pointer',
              }}
            >
              {t.fd_revoke}
            </button>
            <button
              type="button"
              onClick={handleDisable}
              disabled={submitting}
              data-testid="fd-disable-btn"
              style={{
                flex: '1 1 100%',
                background: 'none',
                border: 'none',
                padding: '6px 0',
                fontSize: '10px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--kahla-text-light)',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                marginTop: 4,
              }}
            >
              {t.fd_disable_cta}
            </button>
          </div>

          <AuditList entries={status.audit || []} t={t} lang={lang} />
        </div>
      )}

      {showInvite && (
        <InviteModal
          t={t}
          initialEmail={board?.client_email || status?.invitation?.email || ''}
          loading={submitting}
          onClose={() => { setShowInvite(false); setError(''); }}
          onSubmit={handleInvite}
          errorText={error}
        />
      )}
    </div>
  );
};

export default FinalDecisionPanel;
